import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { LuCheck } from 'react-icons/lu';
import Layout from './Layout';
import { fetchReactionPage, updateReaction } from '../services/api';
import { formatTimeRange } from '../utils/dateFormatter';
import { colors } from '../styles/colors';

const PageContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 48px 32px;
  max-width: 600px;
  width: 100%;
  min-height: 555px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Description = styled.p`
  text-align: center;
  color: ${colors.textLight};
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const VisitorInfo = styled.div`
  background: ${colors.infoBg};
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 32px;
`;

const InfoList = styled.ul`
  list-style: disc;
  padding-left: 20px;
`;

const InfoItem = styled.li`
  font-size: 14px;
  line-height: 1.8;
  color: ${colors.text};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReactionButton = styled.button`
  padding: 16px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s;

  background: ${({ $outlined }) => ($outlined ? 'white' : '#00BBB5')};
  color: ${({ $outlined }) => ($outlined ? '#00BBB5' : 'white')};
  border: ${({ $outlined }) => ($outlined ? '2px solid #00BBB5' : 'none')};

  &:hover:not(:disabled) {
    background: ${({ $outlined }) => ($outlined ? '#e6faf8' : '#00a89f')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 14px;
    font-size: 15px;
  }
`;

const Loading = styled.div`
  text-align: center;
  color: ${colors.textLight};
  font-size: 16px;
`;

const CompleteContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 64px 32px;
  max-width: 500px;
  width: 100%;
  min-height: 555px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const IconCircle = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #00BBB5;
`;

const CompleteTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${colors.text};
`;

const CompleteMessage = styled.p`
  font-size: 14px;
  color: ${colors.textLight};
`;

const ReactionPage = () => {
  const { uid } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [notificationReaction, setNotificationReaction] = useState(null);
  const [settingApp, setSettingApp] = useState(null);
  const [reactionOptions, setReactionOptions] = useState([]);
  const [notificationType, setNotificationType] = useState(null);
  const [buttonText, setButtonText] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // GET /api/reaction_pages/:uid を呼び出し
        const data = await fetchReactionPage(uid);
        const status = data.status;

        if (status === 'expired') {
          history.replace(`/r/${uid}/expired`);
          return;
        } else if (status === 'responded') {
          history.replace(`/r/${uid}/responded`);
          return;
        } else if (status === 'pending') {
          setAppointment(data.appointment || null);
          setVisitors(data.visitors || []);
          setNotificationReaction(data);
          setSettingApp(data.setting_app || null);
          setReactionOptions(data.reaction_options || []);
          setNotificationType(data.notification_type || null);
          setButtonText(data.button_text || null);
        } else {
          history.replace(`/r/${uid}/error`);
          return;
        }
      } catch (error) {
        console.error('Failed to fetch reaction page:', error);
        const errorMessage = error.response?.data?.error?.message;
        history.replace(`/r/${uid}/error`, errorMessage ? { message: errorMessage } : undefined);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [uid, history]);

  const handleReaction = async (reactionOptionUid) => {
    setSubmitting(true);
    try {
      // PUT /api/notification_reactions/:uid を呼び出し
      await updateReaction(uid, reactionOptionUid);
      setCompleted(true);
    } catch (error) {
      const errorType = error.response?.data?.error;
      if (errorType === 'expired') {
        history.push(`/r/${uid}/expired`);
      } else if (errorType === 'already_responded') {
        history.push(`/r/${uid}/responded`);
      } else {
        const errorMessage = error.response?.data?.error?.message;
        history.push(`/r/${uid}/error`, errorMessage ? { message: errorMessage } : undefined);
      }
    }
  };

  const buildInfoItems = () => {
    const items = [];

    if (notificationType === 'appointment' || notificationType === 'touchless') {
      if (appointment) {
        if (appointment.begin_at && appointment.end_at) {
          items.push(formatTimeRange(appointment.begin_at, appointment.end_at));
        }
        if (appointment.title) {
          items.push(appointment.title);
        }
        if (appointment.place) {
          items.push(appointment.place);
        }
      }
    } else if (buttonText) {
      items.push(`${buttonText} ボタン`);
    }

    if (visitors && visitors.length > 0) {
      visitors.forEach((visitor) => {
        const name = visitor.company_name
          ? `${visitor.company_name} ${visitor.name} 様`
          : `${visitor.name} 様`;
        items.push(name);
      });
      if (visitors.length - 1 > 0) {
        items.push(`${visitors.length - 1} 名のお連れ様`);
      }
    }

    // if (settingApp && settingApp.tablet_location) {
    //   items.push(settingApp.tablet_location);
    // }

    return items;
  };

  if (loading) {
    return (
      <Layout>
        <Loading>読み込み中...</Loading>
      </Layout>
    );
  }

  if (completed) {
    return (
      <Layout>
        <CompleteContainer>
          <IconCircle>
            <LuCheck size={48} color="#00BBB5" />
          </IconCircle>
          <CompleteTitle>リアクションが送信されました</CompleteTitle>
          <CompleteMessage>この画面は閉じてください。</CompleteMessage>
        </CompleteContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer>
        <Title>来客リアクション</Title>
        <Description>
          以下の来客にリアクションしてください。<br />受付のiPadにお客様にメッセージが表示されます。
        </Description>

        <VisitorInfo>
          <InfoList>
            {buildInfoItems().map((item, index) => (
              <InfoItem key={index}>{item}</InfoItem>
            ))}
          </InfoList>
        </VisitorInfo>

        <ButtonGroup>
          {reactionOptions.map((option, index) => (
            <ReactionButton
              key={option.uid}
              $outlined={index > 0}
              onClick={() => handleReaction(option.uid)}
              disabled={submitting}
            >
              {option.name}
            </ReactionButton>
          ))}
        </ButtonGroup>
      </PageContainer>
    </Layout>
  );
};

export default ReactionPage;
